import { useState } from 'react'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'

interface FormData {
  name:             string
  email:            string
  phone:            string
  child_age:        string
  desired_start:    string
  message:          string
}

type Status = 'idle' | 'loading' | 'success' | 'error'

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>()

  const onSubmit = async (data: FormData) => {
    setStatus('loading')
    setErrorMsg('')
    try {
      await axios.post('/api/contact', data)
      setStatus('success')
      reset()
    } catch (err: unknown) {
      setStatus('error')
      if (axios.isAxiosError(err)) {
        setErrorMsg(err.response?.data?.detail ?? err.message ?? 'Something went wrong. Please try again or call us directly.')
      } else {
        setErrorMsg('Something went wrong. Please try again or call us directly.')
      }
    }
  }

  if (status === 'success') {
    return (
      <div className="card text-center py-12" role="alert" aria-live="polite">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4 mx-auto">
          <CheckCircle size={32} />
        </div>
        <h3 className="text-brand-navy font-display font-bold text-2xl mb-2">Message Received!</h3>
        <p className="text-slate-500 mb-6 max-w-sm mx-auto">
          Thank you for reaching out! We'll be in touch shortly to schedule your personal tour or answer your questions.
        </p>
        <button onClick={() => setStatus('idle')} className="btn-primary">
          Send Another Message
        </button>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      aria-label="Contact form"
      className="space-y-5"
    >
      {/* Name + Phone */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="label">
            Parent / Guardian Name <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            placeholder="Jane Smith"
            className={`input-field ${errors.name ? 'border-red-400 focus:ring-red-400' : ''}`}
            aria-required="true"
            aria-describedby={errors.name ? 'name-error' : undefined}
            {...register('name', { required: 'Your name is required.' })}
          />
          {errors.name && (
            <p id="name-error" className="mt-1 text-xs text-red-500" role="alert">{errors.name.message}</p>
          )}
        </div>
        <div>
          <label htmlFor="phone" className="label">Phone Number</label>
          <input
            id="phone"
            type="tel"
            autoComplete="tel"
            placeholder="(317) 555-1234"
            className="input-field"
            {...register('phone')}
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="label">
          Email Address <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="jane@example.com"
          className={`input-field ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
          aria-required="true"
          aria-describedby={errors.email ? 'email-error' : undefined}
          {...register('email', {
            required: 'Email address is required.',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email.' },
          })}
        />
        {errors.email && (
          <p id="email-error" className="mt-1 text-xs text-red-500" role="alert">{errors.email.message}</p>
        )}
      </div>

      {/* Child age + Start date */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="child_age" className="label">
            Child's Age / Date of Birth
          </label>
          <input
            id="child_age"
            type="text"
            placeholder="e.g. 18 months, 3 years"
            className="input-field"
            {...register('child_age')}
          />
        </div>
        <div>
          <label htmlFor="desired_start" className="label">Desired Start Date</label>
          <input
            id="desired_start"
            type="date"
            className="input-field appearance-none"
            {...register('desired_start')}
          />
        </div>
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="label">
          Questions or Message <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <textarea
          id="message"
          rows={5}
          placeholder="Tell us a bit about your child and what you're looking for. We'd love to hear from you!"
          className={`input-field resize-none ${errors.message ? 'border-red-400 focus:ring-red-400' : ''}`}
          aria-required="true"
          aria-describedby={errors.message ? 'message-error' : undefined}
          {...register('message', { required: 'Please include a message or question.' })}
        />
        {errors.message && (
          <p id="message-error" className="mt-1 text-xs text-red-500" role="alert">{errors.message.message}</p>
        )}
      </div>

      {/* API error */}
      {status === 'error' && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4" role="alert" aria-live="assertive">
          <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-red-700">{errorMsg}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="btn-primary w-full py-4 text-base disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === 'loading' ? (
          <>
            <Loader2 size={18} className="animate-spin" aria-hidden="true" />
            Sending…
          </>
        ) : (
          <>
            <Send size={18} aria-hidden="true" />
            Send Message
          </>
        )}
      </button>

      <p className="text-center text-xs text-slate-400">
        We respect your privacy and will never share your information.
      </p>
    </form>
  )
}
